import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Button, Badge, Spinner, Table, Row, Col, Container } from 'react-bootstrap';
import AdministrationDashboard from './AdministrationDashboard';
import 'bootstrap/dist/css/bootstrap.min.css';

// Utility function to group products by order
const groupOrders = (flatOrders) => {
  const grouped = {};
  flatOrders.forEach((row) => {
    const orderId = row.list_item_id;
    if (!grouped[orderId]) {
      grouped[orderId] = {
        ...row,
        products: [],
      };
    }
    grouped[orderId].products.push({
      productName: row.product_name,
      quantity: row.quantity,
      price: row.selling_price,
      pmode: row.pmode,
    });
  });
  // Now remove duplicate order-level fields from products array
  return Object.values(grouped);
};

const getStatusBadgeColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'new order':
      return 'info';
    case 'in progress':
      return 'warning';
    case 'completed':
      return 'success';
    default:
      return 'secondary';
  }
};

const MarketPlaceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [trips, setTrips] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('http://localhost:3009/salelist/orderhistory/3');
      // Group products by order
      const groupedOrders = groupOrders(response.data);

      setOrders(groupedOrders);

      // Fetch unique trip details
      const uniqueTripIds = [...new Set(groupedOrders.map(order => order.trip_id))].filter(Boolean);
      const tripDetails = {};
      await Promise.all(
        uniqueTripIds.map(async (tripId) => {
          const tripResponse = await axios.get(`https://srv547457.hstgr.cloud:3011/trip/3`);
          tripDetails[tripId] = tripResponse.data[0];
        })
      );
      setTrips(tripDetails);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = (tripId, order) => {
    navigate(`/TripStatus/${tripId}`, {
      state: {
        tripDetails: trips[tripId],
        orderDetails: order,
      },
    });
  };

  if (loading) {
    return (
      <AdministrationDashboard>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
          <Spinner animation="border" style={{ color: '#666' }} />
        </div>
      </AdministrationDashboard>
    );
  }

  return (
    <AdministrationDashboard>
      <Container fluid className="py-4" style={{ backgroundColor: '#f6f7fb', minHeight: '100vh' }}>
        <Row className="align-items-center mb-4">
          <Col xs={12} md={6}>
            <h2 className="fw-bold text-dark mb-2 mb-md-0" style={{ letterSpacing: '.01em' }}>
              Marketplace Orders
            </h2>
          </Col>
          <Col xs={12} md="auto" className="text-md-end">
            <Badge bg="dark" className="fs-6 px-3 py-2">{orders.length} Orders</Badge>
          </Col>
        </Row>
        <Row>
          {orders.length === 0 && (
            <Col xs={12} className="text-center py-5">
              <p className="text-muted fs-5">No orders found.</p>
            </Col>
          )}
          {orders.map((order) => (
            <Col xs={12} key={order.list_item_id} className="mb-4">
              <Card className="shadow-sm border-0" style={{ borderRadius: '22px', overflow: 'hidden', background: '#fff' }}>
                <Card.Header
                  className="bg-white border-bottom-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center px-4 py-3"
                  style={{ background: 'linear-gradient(90deg, #f8fafc 60%, #f0f4f9 100%)' }}
                >
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="fw-bold fs-5 text-dark">Order #{order.list_item_id}</span>
                    <Badge bg="light" text="dark" className="ms-2 fs-6 shadow-sm border">
                      {order.sale_date ? new Date(order.sale_date).toLocaleDateString() : 'N/A'}
                    </Badge>
                    {order.trip_id && trips[order.trip_id] && (
                      <Badge
                        bg={getStatusBadgeColor(trips[order.trip_id].status)}
                        className="ms-2 fs-6"
                        style={{ minWidth: 90 }}
                      >
                        {trips[order.trip_id].status}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="rounded-pill mt-3 mt-md-0 px-4 shadow-sm"
                    onClick={() => handleUpdateStatus(order.trip_id, order)}
                    disabled={!order.trip_id}
                  >
                    Update Trip Status
                  </Button>
                </Card.Header>
                <Card.Body className="p-4 pb-2">
                  <Row>
                    {/* Order Products */}
                    <Col xs={12} md={7} className="mb-4 mb-md-0">
                      <h6 className="fw-semibold text-secondary mb-3">Products</h6>
                      <div className="table-responsive">
                        <Table hover borderless className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 60 }}>#</th>
                              <th>Product</th>
                              <th className="text-center">Qty</th>
                              <th className="text-end">Price</th>
                              <th className="text-end">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.products.map((product, idx) => (
                              <tr key={idx}>
                                <td className="text-muted">{idx + 1}</td>
                                <td className="fw-semibold">{product.productName}</td>
                                <td className="text-center">{product.quantity}</td>
                                <td className="text-end">${Number(product.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                <td className="text-end fw-semibold text-primary">
                                  ${(product.quantity * product.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                            <tr className="table-light">
                              <td colSpan={4} className="text-end fw-bold text-secondary">Total</td>
                              <td className="text-end fw-bold fs-5 text-primary">
                                ${order.products.reduce((sum, p) => sum + (p.quantity * p.price), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>

                    {/* Trip Details */}
                    <Col xs={12} md={5}>
                      <h6 className="fw-semibold text-secondary mb-3">Trip Details</h6>
                      <div className="bg-light rounded-4 p-3">
                        {order.trip_id && trips[order.trip_id] ? (
                          <Table borderless size="sm" className="mb-0">
                            <tbody>
                              <tr>
                                <td className="text-muted">Status</td>
                                <td>
                                  <Badge
                                    bg={getStatusBadgeColor(trips[order.trip_id].status)}
                                    className="fs-6"
                                  >
                                    {trips[order.trip_id].status}
                                  </Badge>
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted">Origin</td>
                                <td>{trips[order.trip_id].origin_location}</td>
                              </tr>
                              <tr>
                                <td className="text-muted">Destination</td>
                                <td>{trips[order.trip_id].dest_location}</td>
                              </tr>
                              <tr>
                                <td className="text-muted">Distance</td>
                                <td>{trips[order.trip_id].distance} km</td>
                              </tr>
                            </tbody>
                          </Table>
                        ) : (
                          <div className="text-muted small">No trip details available.</div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer
                  className="text-muted px-4 py-3"
                  style={{
                    background: 'linear-gradient(90deg, #f8fafc 60%, #f0f4f9 100%)',
                    borderTop: 'none',
                  }}
                >
                  <Row className="align-items-center">
                    <Col xs={12} md={6} className="small">
                      Processed by: <span className="fw-semibold text-dark">{order.username}</span>
                    </Col>
                    <Col xs={12} md={6} className="small text-md-end mt-2 mt-md-0">
                      Order ID: <span className="fw-semibold text-dark">{order.list_item_id}</span>
                    </Col>
                  </Row>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      <style>{`
        .table-responsive {
          min-width: 100%;
        }
        @media (max-width: 767px) {
          .table thead {
            display: none;
          }
          .table tbody tr {
            display: flex;
            flex-direction: column;
            margin-bottom: 1rem;
            border-radius: 10px;
            background: #f9fafb;
            box-shadow: 0 2px 4px rgba(0,0,0,0.04);
          }
          .table tbody td {
            display: flex;
            justify-content: space-between;
            padding: .5rem 1rem;
            border: none;
          }
        }
      `}</style>
    </AdministrationDashboard>
  );
};

export default MarketPlaceOrders;